import { motion } from 'framer-motion';
import {
  DollarSign,
  Calendar,
  Percent,
  CreditCard,
  Building2,
  Calculator,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { formatCurrency, formatPercentage } from '../../utils';

const financingTypeInfo = {
  Lease: {
    icon: Building2,
    color: 'from-blue-600 to-blue-700',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    description: 'Lower monthly payments, return or purchase at end of term',
  },
  Loan: {
    icon: CreditCard,
    color: 'from-purple-600 to-purple-700',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    description: 'Build equity and own the equipment outright',
  },
  Rental: {
    icon: Calendar,
    color: 'from-amber-600 to-amber-700',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    description: 'Maximum flexibility with short-term commitment',
  },
  Cash: {
    icon: DollarSign,
    color: 'from-green-600 to-green-700',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    description: 'Pay upfront and own equipment immediately',
  },
};

export function FinancingSection() {
  const proposal = useProposalData();

  if (!proposal.sections.showFinancing || !proposal.financing) {
    return null;
  }

  const { financing, pricing } = proposal;
  const typeInfo = financingTypeInfo[financing.type];
  const Icon = typeInfo.icon;

  // Calculate total ourselves - don't trust pricing.total from JSON
  const calculatedSubtotal = pricing.equipmentSubtotal + pricing.addonsSubtotal + pricing.servicesSubtotal;
  const calculatedSavings = pricing.tradeInCredit + pricing.discounts;
  const calculatedTotal = calculatedSubtotal - calculatedSavings;

  // Calculate total financed amount
  const totalFinanced = calculatedTotal - (financing.downPayment || 0);

  return (
    <Section id="financing" dark pattern="grid">
      <SectionHeader
        title="FINANCING OPTIONS"
        subtitle="Payment Terms"
        dark
      />

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Main Financing Card */}
        <motion.div
          className="lg:col-span-3 bg-white rounded-2xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${typeInfo.color} p-8`}>
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-white/80 text-sm uppercase tracking-wider mb-1">
                  Selected Payment Method
                </div>
                <h3 className="font-display text-4xl text-white tracking-wide">
                  {financing.type.toUpperCase()}
                </h3>
                <p className="text-white/80 mt-1">{typeInfo.description}</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-8">
            {/* Monthly Payment Highlight - Hidden for Cash deals */}
            {financing.type !== 'Cash' && financing.monthlyPayment && (
              <div className="mb-8 p-6 bg-hul-navy/5 rounded-xl border-l-4 border-hul-red">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider mb-1">
                      Monthly Payment
                    </div>
                    <div className="font-mono text-5xl font-bold text-hul-red">
                      {formatCurrency(financing.monthlyPayment)}
                    </div>
                    {financing.term && (
                      <div className="text-gray-600 mt-2">
                        for {financing.term} months
                      </div>
                    )}
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-hul-navy">
                    <Calculator className="w-6 h-6" />
                  </div>
                </div>
              </div>
            )}

            {/* Terms Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {financing.term && (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <Calendar className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-hul-navy">{financing.term}</div>
                  <div className="text-xs text-gray-500 uppercase">Months</div>
                </div>
              )}

              {financing.rate && (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <Percent className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-hul-navy">{formatPercentage(financing.rate)}</div>
                  <div className="text-xs text-gray-500 uppercase">APR</div>
                </div>
              )}

              {financing.downPayment && (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <DollarSign className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-hul-navy">
                    {formatCurrency(financing.downPayment, false)}
                  </div>
                  <div className="text-xs text-gray-500 uppercase">Down Payment</div>
                </div>
              )}

              {financing.residual && (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <Building2 className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-hul-navy">{financing.residual}%</div>
                  <div className="text-xs text-gray-500 uppercase">Residual</div>
                </div>
              )}
            </div>

            {/* Amount Financed */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center text-gray-600">
                <span>Total Equipment Value</span>
                <span className="font-mono">{formatCurrency(calculatedTotal)}</span>
              </div>
              {financing.downPayment && (
                <div className="flex justify-between items-center text-gray-600 mt-2">
                  <span>Down Payment</span>
                  <span className="font-mono text-green-600">-{formatCurrency(financing.downPayment)}</span>
                </div>
              )}
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <span className="font-heading text-hul-navy">Amount Financed</span>
                <span className="font-mono text-2xl font-bold text-hul-navy">
                  {formatCurrency(totalFinanced)}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Benefits Card */}
        <motion.div
          className="lg:col-span-2 space-y-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          {/* Benefits */}
          <div className={`${typeInfo.bgColor} rounded-xl p-6`}>
            <h4 className={`font-heading text-lg uppercase tracking-wider ${typeInfo.textColor} mb-4`}>
              {financing.type} Benefits
            </h4>
            <ul className="space-y-3">
              {getBenefits(financing.type).map((benefit, i) => (
                <li key={i} className={`flex items-start gap-3 ${typeInfo.textColor}`}>
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact CTA */}
          <div className="bg-white rounded-xl p-6">
            <h4 className="font-heading text-lg text-hul-navy mb-3">
              Questions About Financing?
            </h4>
            <p className="text-gray-600 text-sm mb-4">
              Our finance team can help you find the best option for your business needs.
            </p>
            <a
              href={`mailto:${proposal.salesperson.email}?subject=Financing Question - ${proposal.proposal.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-hul-navy text-white rounded-lg hover:bg-hul-navy-dark transition-colors text-sm"
            >
              Contact Finance Team
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Disclaimer */}
          <div className="text-xs text-white/50 leading-relaxed">
            * Payment amounts are estimates based on approved credit. Actual terms
            may vary based on credit approval and current rates. Contact us for
            a personalized quote.
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

function getBenefits(type: string): string[] {
  switch (type) {
    case 'Lease':
      return [
        'Lower monthly payments vs. purchasing',
        'Preserve capital for other investments',
        'Potential tax advantages (Section 179)',
        'Flexible end-of-term options',
        'Keep equipment current with upgrades',
      ];
    case 'Loan':
      return [
        'Build equity in your equipment',
        'Own the equipment at term end',
        'Fixed monthly payments',
        'Full depreciation benefits',
        'No mileage or usage restrictions',
      ];
    case 'Rental':
      return [
        'No long-term commitment',
        'Maintenance included',
        'Easy to scale up or down',
        'Tax-deductible expense',
        'Try before you buy',
      ];
    case 'Cash':
      return [
        'No interest charges',
        'Immediate ownership',
        'Potential early-pay discounts',
        'Simplified accounting',
        'Maximum negotiating leverage',
      ];
    default:
      return [];
  }
}
